import * as React from 'react';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { Link } from 'gatsby';

import { useUiComponentsInternationalContext } from '@repo/static-translations';
import { cn } from '../../../tailwind-base/utils';
import { ButtonProps, buttonVariants } from '../Button';

const Pagination = ({ className, ...props }: React.ComponentProps<'nav'>) => {
  const { tt } = useUiComponentsInternationalContext();
  return (
    <nav
      role="navigation"
      aria-label={tt(s => s.shadcnCommon.paginationAriaLabel)}
      className={cn('mx-auto flex w-full justify-center', className)}
      {...props}
    />
  );
};
Pagination.displayName = 'Pagination';

const PaginationContent = React.forwardRef<
  HTMLUListElement,
  React.HTMLAttributes<HTMLUListElement>
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn('flex flex-row items-center gap-1 list-none pl-0', className)}
    {...props}
  />
));
PaginationContent.displayName = 'PaginationContent';

const PaginationItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<'li'>
>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn('', className)} {...props} />
));
PaginationItem.displayName = 'PaginationItem';

type PaginationLinkProps = {
  className?: string;
  isActive?: boolean;
  disabled?: boolean;
  to: string;
} & Pick<ButtonProps, 'size'> &
  React.ComponentProps<typeof Link>;

const PaginationLink = ({
  className,
  isActive,
  size = 'icon',
  id = '',
  to,
  ...props
}: PaginationLinkProps) => (
  <Link
    to={to}
    aria-current={isActive ? 'page' : undefined}
    className={cn(
      '!rounded-interface no-underline',
      buttonVariants({
        variant: isActive ? 'primary' : 'ghost',
        size,
      }),
      id !== 'previous-page' && id !== 'next-page'
        ? 'hover:bg-primary hover:text-primary-foreground'
        : '',
      className
    )}
    {...props}
  >
    {props.children}
  </Link>
);
PaginationLink.displayName = 'PaginationLink';

const PaginationPrevious = ({
  className,
  disabled,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => {
  const { tt } = useUiComponentsInternationalContext();
  return (
    <PaginationLink
      id="previous-page"
      aria-label={tt(s => s.shadcnCommon.paginationPreviousAriaLabel)}
      size="default"
      className={cn(
        '!rounded-md gap-1 sm:pl-2.5',
        className,
        disabled ? 'pointer-events-none opacity-50' : ''
      )}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : undefined}
      {...props}
    >
      <MdChevronLeft className="h-4 w-4" />
      <span className="hidden sm:block">
        {tt(s => s.shadcnCommon.paginationPreviousLabel)}
      </span>
    </PaginationLink>
  );
};
PaginationPrevious.displayName = 'PaginationPrevious';

const PaginationNext = ({
  className,
  disabled,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => {
  const { tt } = useUiComponentsInternationalContext();
  return (
    <PaginationLink
      id="next-page"
      aria-label={tt(s => s.shadcnCommon.paginationNextAriaLabel)}
      size="default"
      className={cn(
        '!rounded-md gap-1 sm:pr-2.5',
        className,
        disabled ? 'pointer-events-none opacity-50' : ''
      )}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : undefined}
      {...props}
    >
      <span className="hidden sm:block">
        {tt(s => s.shadcnCommon.paginationNextLabel)}
      </span>
      <MdChevronRight className="h-4 w-4" />
    </PaginationLink>
  );
};
PaginationNext.displayName = 'PaginationNext';

const PaginationEllipsis = ({
  className,
  ...props
}: React.ComponentProps<'span'>) => {
  const { tt } = useUiComponentsInternationalContext();
  return (
    <span
      aria-hidden
      className={cn('flex h-9 w-9 items-center justify-center', className)}
      {...props}
    >
      <span>&hellip;</span>
      <span className="sr-only">
        {tt(s => s.shadcnCommon.paginationEllipsisLabel)}
      </span>
    </span>
  );
};
PaginationEllipsis.displayName = 'PaginationEllipsis';

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
};
